import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientMediaRequest, ClientMediaResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { LowDataBadgeComponent } from '../../../../shared/components/sette/low-data-badge/low-data-badge.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, noMentionsLabel, isLowDataCount } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsAxisDate, brandColor, MEDIA_COLORS, SENTIMENT_COLORS, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateTopic } from '../shared/taxonomy.util';

interface TopMediaRowView {
	rank: number;
	name: string;
	type: string;
	typeColorClass: string;
	mentions: number;
	change: string;
	changeTone: Tone;
	reach: string;
	impact: string;
	impactTone: Tone;
}

interface MediaTypeRowView {
	name: string;
	value: number;
	percentage: string;
	colorClass: string;
	dotColor: string;
}

interface MediaSentimentRowView {
	outlet: string;
	mentions: number;
	posPct: number;
	neuPct: number;
	negPct: number;
	// True only when there's genuinely no impact-scored mention to split (backend never gates
	// positivePercent/neutralPercent/negativePercent by mention count - see
	// ClientAnalyticsServiceImpl#buildMediaRows). Never true merely because `mentions` is small.
	noData: boolean;
	// Styling-only: bar still shown in full, just paled + a small badge (isLowDataCount).
	lowData: boolean;
}

interface TopStoryView {
	title: string;
	media: string;
	brand: string;
	reach: string;
	impact: string;
	impactTone: Tone;
	sentiment: 'pos' | 'neu' | 'neg';
}

// SETTE redesign (CLIPPING-598), Правило А: media type is always MEDIA_COLORS (tint bg + text),
// never an arbitrary categorical color. Brand identity in the mentions-by-media-and-brand matrix
// keeps the categorical palette (avoiding chart-5, since this screen also shows Impact).
const MEDIA_TYPE_LABEL_KEYS: Record<string, string> = {
	WEB: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_WEB',
	TV: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_TV',
	PRINTED: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_PRINT',
	RADIO: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_RADIO',
	SOCIAL: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_SOCIAL',
};
const MEDIA_TYPE_COLOR_CLASS: Record<string, string> = {
	TV: 'bg-media-tint-tv text-media-tv',
	WEB: 'bg-media-tint-portal text-media-portal',
	PRINTED: 'bg-media-tint-print text-media-print',
	SOCIAL: 'bg-media-tint-social text-media-social',
	RADIO: 'bg-media-tint-radio text-media-radio',
};
const MEDIA_TYPE_DOT_COLOR: Record<string, string> = { WEB: MEDIA_COLORS.portal, TV: MEDIA_COLORS.tv, SOCIAL: MEDIA_COLORS.social, PRINTED: MEDIA_COLORS.print, RADIO: MEDIA_COLORS.radio };

// Ported from analytics-design/src/components/MediaDashboard.tsx, then rewired to call
// POST /v1/client/analytics/media instead of using sample data. The Media Type/Language selects
// and "Manage Media List" were visual-only exactly as in the original design - its own header
// comment already notes none of them ever filtered the sample data either - and are now hidden
// (2026-10-06, product decision 2026-10-05: not in v1), kept behind @if (false), not deleted.
// The Filters modal is unaffected, still visual-only as before. "Outlets with 3+ mentions" and
// "Unique Media Outlets" (Задача 9) are backend-computed over every one of the focus brand's
// mentions, not the (at most 15) top-media rows - see ClientAnalyticsServiceImpl#countMediaOutlets.
@Component({
	selector: 'app-media',
	standalone: true,
	imports: [FormsModule, MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent, LowDataBadgeComponent],
	templateUrl: './media.component.html',
	styleUrl: './media.component.scss',
})
export class MediaComponent implements OnChanges {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientMediaResponse | null = null;

	selectedMediaType = 'All';
	selectedLanguage = 'All';
	showManageModal = false;
	showFiltersModal = false;
	manageMediaSearch = '';
	private readonly fetchTrigger = new Subject<ClientMediaRequest>();

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getMediaAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.mentions?.value ?? 0) === 0;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientMediaRequest);
	}

	openManageModal(): void {
		this.showManageModal = true;
	}

	closeManageModal(): void {
		this.showManageModal = false;
	}

	openFiltersModal(): void {
		this.showFiltersModal = true;
	}

	closeFiltersModal(): void {
		this.showFiltersModal = false;
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	mediaTypeLabel(code: string): string {
		const key = MEDIA_TYPE_LABEL_KEYS[code];
		return key ? this.translate.instant(key) : code;
	}

	// --- KPI row ---

	// CLIPPING-5: these 4 KPIs used to be computed here, from the (up to 15) topMedia rows -
	// Mentions came out smaller than Overview's, and Avg Visibility/Avg Impact/Net Sentiment were a
	// weighted average over those same 15 outlets, not every mention. The backend now computes all
	// four over every mention in the filter, through the exact same buildFocusKpis Overview uses -
	// so they read straight off the response, same as Overview's own KPI row.
	get totalMentions(): number {
		return Math.round(this.data?.mentions?.value ?? 0);
	}

	get sparkMentions(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.mentions ?? 0);
	}

	get avgReach(): string {
		const v = this.data?.avgReach?.value;
		return v != null ? `${formatAnalyticsNumber(v, this.lang, 1)}/100` : '—';
	}

	get sparkReach(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.avgReach ?? 0);
	}

	get avgImpact(): string {
		const v = this.data?.avgImpact?.value;
		return v != null ? formatSigned(v, 1, this.lang) : '—';
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	toneTextClass(tone: Tone): string {
		if (tone === 'positive') return 'text-positive';
		if (tone === 'negative') return 'text-negative';
		return 'text-ink-subtle';
	}

	get sparkImpact(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.avgImpact ?? 0);
	}

	// Задача 9: backend-computed over every one of the focus brand's mentions in the filter, not
	// just the (at most 15) topMedia rows - a period with more than 15 distinct outlets used to
	// undercount both of these when they were derived from topMedia.length/filter here.
	get outletsWith3PlusMentions(): number {
		return this.data?.outletsWith3PlusMentions ?? 0;
	}

	get uniqueMediaOutlets(): number {
		return this.data?.uniqueMediaOutlets ?? 0;
	}

	get netSentimentValue(): string {
		const v = this.data?.netSentiment?.value;
		return v != null ? `${formatSigned(v, 1, this.lang)}%` : '—';
	}

	// Per-bar tooltip note (reach/impact bar charts): that one outlet's own mention count.
	private mediaSampleNote(row: { mentions?: number } | undefined): string {
		const count = formatAnalyticsInt(row?.mentions ?? 0, this.lang);
		return this.translate.instant('ANALYTICS_MEDIA.SAMPLE_NOTE', { count });
	}

	// --- Widget 1: Top media table ---

	get topMediaTable(): TopMediaRowView[] {
		return (this.data?.topMedia ?? []).map((m, i) => ({
			rank: i + 1,
			name: m.name || '',
			type: this.mediaTypeLabel(m.type || ''),
			typeColorClass: MEDIA_TYPE_COLOR_CLASS[m.type || ''] || 'bg-surface-gray text-ink-muted',
			mentions: m.mentions ?? 0,
			change: this.changeStr(m.mentions, m.previousMentions),
			changeTone: m.mentions != null && m.previousMentions != null ? signTone(m.mentions - m.previousMentions) : 'neutral',
			reach: m.avgReach != null ? `${formatAnalyticsNumber(m.avgReach, this.lang, 1)}/100` : '—',
			impact: m.avgImpact != null ? formatSigned(m.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(m.avgImpact ?? 0),
		}));
	}

	private changeStr(current?: number, previous?: number | null): string {
		if (current == null || previous == null) return '—';
		const diff = current - previous;
		return `${formatSignedAuto(diff, this.lang)} (${formatAnalyticsInt(previous, this.lang)} → ${formatAnalyticsInt(current, this.lang)})`;
	}

	get topMediaFooter(): string {
		const rows = this.topMediaTable.slice(0, 7);
		const sum = rows.reduce((s, r) => s + r.mentions, 0);
		const total = this.totalMentions;
		if (total === 0) return this.translate.instant('ANALYTICS_MEDIA.NO_OUTLET_DATA');
		return this.translate.instant('ANALYTICS_MEDIA.TOP_MEDIA_FOOTER', {
			count: rows.length,
			percent: formatAnalyticsNumber((sum / total) * 100, this.lang, 1),
			sum: formatAnalyticsInt(sum, this.lang),
			total: formatAnalyticsInt(total, this.lang),
		});
	}

	// --- Widget 2: Mentions trend ---

	// SETTE redesign (CLIPPING-598), Правило А: Mentions (volume) and Avg Visibility (0-100) carry
	// no sign; Avg Impact IS signed, so its points carry the sign via signTone while the line
	// itself stays blue. Trend colors follow-up (2026-11-01): Mentions and Impact were both the
	// same dark blue (#244896) and hard to tell apart - same fix as Overview's trend chart:
	// Mentions is now neutral gray, Visibility is orange, Impact stays dark blue.
	get mentionsTrendChartData() {
		const trend = this.data?.trend ?? [];
		const labels = trend.map((t) => (t.bucketStart ? formatAnalyticsAxisDate(new Date(t.bucketStart), this.lang) : ''));
		return {
			labels,
			datasets: [
				{ label: this.translate.instant('ANALYTICS_MEDIA.TOTAL_MENTIONS_LEGEND'), data: trend.map((t) => t.mentions ?? 0), borderColor: '#9AA1AD', backgroundColor: 'rgba(154, 161, 173, 0.12)', borderWidth: 2.2, tension: 0.35, pointRadius: 3, pointBackgroundColor: '#9AA1AD', fill: true, yAxisID: 'yMentions' },
				{ label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX'), data: trend.map((t) => t.avgReach ?? null), borderColor: '#F59E0B', backgroundColor: 'transparent', borderWidth: 2, borderDash: [4, 3], tension: 0.35, pointRadius: 2.5, pointBackgroundColor: '#F59E0B', yAxisID: 'yScores', spanGaps: true },
				{
					label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE'),
					data: trend.map((t) => t.avgImpact ?? null),
					borderColor: '#244896',
					backgroundColor: 'transparent',
					borderWidth: 2,
					tension: 0.35,
					pointRadius: 2.5,
					pointBackgroundColor: trend.map((t) => SENTIMENT_COLORS[signTone(t.avgImpact ?? 0)]),
					yAxisID: 'yScores',
					spanGaps: true,
				},
			],
		};
	}

	get mentionsTrendChartOptions() {
		const maxMentions = Math.max(4, ...(this.data?.trend ?? []).map((t) => t.mentions ?? 0));
		return {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index' as const, intersect: false },
			plugins: {
				legend: { display: false },
				tooltip: {
					backgroundColor: AXIS.tooltipBg,
					padding: 8,
					cornerRadius: 10,
					bodyFont: { size: 10 },
					titleFont: { size: 10, weight: 'bold' as const },
					callbacks: {
						label: (ctx: any) => {
							const label = ctx.dataset.label || '';
							const val = ctx.parsed.y;
							if (ctx.datasetIndex === 0) return ` ${label}: ${this.translate.instant('ANALYTICS_TOPICS.SAMPLE_NOTE', { count: formatAnalyticsInt(val, this.lang) })}`;
							if (ctx.datasetIndex === 1) return ` ${label}: ${formatAnalyticsNumber(val, this.lang, 1)} / 100`;
							return ` ${label}: ${formatSignedAuto(val, this.lang)} pts`;
						},
					},
				},
			},
			scales: {
				x: { grid: { display: false }, ticks: { font: { size: 9 }, color: AXIS.text } },
				yMentions: { type: 'linear' as const, position: 'left' as const, min: 0, max: Math.ceil(maxMentions * 1.3), grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text }, title: { display: true, text: this.translate.instant('ANALYTICS_GLOSSARY.MENTIONS'), color: AXIS.text, font: { size: 9, weight: 'bold' as const } } },
				yScores: { type: 'linear' as const, position: 'right' as const, min: -100, max: 100, grid: { display: false }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => formatSignedAuto(Number(v), this.lang) }, title: { display: true, text: this.translate.instant('ANALYTICS_MEDIA.AVG_SCORE_AXIS'), color: AXIS.text, font: { size: 9, weight: 'bold' as const } } },
			},
		};
	}

	// --- Widget 3: Mentions by media type ---

	get mediaTypeData(): MediaTypeRowView[] {
		const rows = this.data?.mediaTypes ?? [];
		const total = rows.reduce((s, r) => s + (r.mentions ?? 0), 0);
		return rows.map((r) => ({
			name: this.mediaTypeLabel(r.type || ''),
			value: r.mentions ?? 0,
			percentage: total > 0 ? `${formatAnalyticsNumber(((r.mentions ?? 0) / total) * 100, this.lang, 1)}%` : '0%',
			colorClass: MEDIA_TYPE_COLOR_CLASS[r.type || ''] || 'bg-surface-gray text-ink-muted',
			dotColor: MEDIA_TYPE_DOT_COLOR[r.type || ''] || '#7D8796',
		}));
	}

	get mediaTypeTotal(): number {
		return this.mediaTypeData.reduce((s, r) => s + r.value, 0);
	}

	get mediaTypeChartData() {
		return { labels: this.mediaTypeData.map((m) => m.name), datasets: [{ data: this.mediaTypeData.map((m) => m.value), backgroundColor: this.mediaTypeData.map((m) => m.dotColor), borderWidth: 2, borderColor: '#ffffff', hoverOffset: 4 }] };
	}

	readonly mediaTypeChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '70%',
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const pct = this.mediaTypeTotal > 0 ? formatAnalyticsNumber((Number(ctx.parsed) / this.mediaTypeTotal) * 100, this.lang, 1) : '0';
						return ` ${ctx.label}: ${this.translate.instant('ANALYTICS_TOPICS.SAMPLE_NOTE', { count: formatAnalyticsInt(ctx.parsed, this.lang) })} (${pct}%)`;
					},
				},
			},
		},
	};

	get mediaTypeFooter(): string {
		const top = [...this.mediaTypeData].sort((a, b) => b.value - a.value)[0];
		return top ? this.translate.instant('ANALYTICS_MEDIA.MEDIA_TYPE_FOOTER', { name: top.name, percentage: top.percentage }) : this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.NO_MEDIA_TYPE_DATA');
	}

	// --- Widget 4: Mentions by media and brand ---

	get mediaBrandColumns(): { key: string; label: string; color: string }[] {
		return (this.data?.mediaByBrand?.columns ?? []).map((c) => ({ key: c.key || '', label: c.label || c.key || '', color: brandColor(c.key, this.brandOptions) }));
	}

	get mediaBrandChartData() {
		const rows = (this.data?.mediaByBrand?.rows ?? []).slice(0, 8);
		const cols = this.mediaBrandColumns;
		return {
			labels: rows.map((r) => r.rowLabel || ''),
			datasets: cols.map((col) => ({
				label: col.label,
				data: rows.map((r) => { const total = r.totalMentions || 1; return ((r.cellsByColumnKey?.[col.key]?.mentions ?? 0) / total) * 100; }),
				backgroundColor: col.color,
				barThickness: 11,
			})),
		};
	}

	readonly mediaBrandChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { display: false }, tooltip: { backgroundColor: AXIS.tooltipBg, padding: 8, cornerRadius: 10, callbacks: { label: (ctx: any) => ` ${ctx.dataset.label}: ${formatAnalyticsInt(ctx.parsed.x, this.lang)}% SOV` } } },
		scales: {
			x: { stacked: true, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, callback: (v: number | string) => `${v}%` } },
			y: { stacked: true, grid: { display: false }, ticks: { font: { size: 10, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 5/6: Visibility / Impact by media ---

	// SETTE redesign (CLIPPING-598), Правило А: Visibility (unsigned) rank-list bars are plain
	// blue; Impact (signed) is the one allowed to diverge by sign, via signTone.
	// Пакет А #10: the filtered chart data below can still be empty if every outlet in topMedia
	// genuinely has no avgReach/avgImpact at all (e.g. no medium-type data) - shown instead of a
	// blank chart. Analytics accuracy review follow-up (2026-11-01): no longer a 10-mention floor
	// case (an outlet appearing in topMedia always has >=1 real mention, which is now enough for
	// a real average), just the plain empty-chart fallback.
	get perOutletInsufficientDataLabel(): string {
		return noMentionsLabel(this.translate);
	}

	get reachMediaChartData() {
		const rows = [...(this.data?.topMedia ?? [])].filter((m) => m.avgReach != null).sort((a, b) => (b.avgReach ?? 0) - (a.avgReach ?? 0)).slice(0, 8);
		return { labels: rows.map((r) => r.name), datasets: [{ label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX'), data: rows.map((r) => r.avgReach ?? 0), backgroundColor: '#244896', borderRadius: 4, barThickness: 12 }] };
	}

	readonly reachMediaChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const rows = [...(this.data?.topMedia ?? [])].filter((m) => m.avgReach != null).sort((a, b) => (b.avgReach ?? 0) - (a.avgReach ?? 0)).slice(0, 8);
						return ` ${this.translate.instant('ANALYTICS_TOPICS.AVG_VISIBILITY_TOOLTIP', { value: formatAnalyticsNumber(ctx.parsed.x, this.lang, 1), note: this.mediaSampleNote(rows[ctx.dataIndex]) })}`;
					},
				},
			},
		},
		scales: { x: { min: 0, max: 100, grid: { color: AXIS.grid }, ticks: { font: { size: 10 }, color: AXIS.text, stepSize: 20 } }, y: { grid: { display: false }, ticks: { font: { size: 10, weight: 'bold' as const }, color: AXIS.text } } },
	};

	get impactMediaChartData() {
		const rows = [...(this.data?.topMedia ?? [])].filter((m) => m.avgImpact != null).sort((a, b) => (b.avgImpact ?? 0) - (a.avgImpact ?? 0)).slice(0, 8);
		const values = rows.map((r) => r.avgImpact ?? 0);
		return { labels: rows.map((r) => r.name), datasets: [{ label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE'), data: values, backgroundColor: values.map((v) => SENTIMENT_COLORS[signTone(v)]), borderRadius: 4, barThickness: 12 }] };
	}

	readonly impactMediaChartOptions = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				backgroundColor: AXIS.tooltipBg,
				padding: 8,
				cornerRadius: 10,
				callbacks: {
					label: (ctx: any) => {
						const rows = [...(this.data?.topMedia ?? [])].filter((m) => m.avgImpact != null).sort((a, b) => (b.avgImpact ?? 0) - (a.avgImpact ?? 0)).slice(0, 8);
						return ` ${this.translate.instant('ANALYTICS_TOPICS.AVG_IMPACT_TOOLTIP', { value: formatSignedAuto(ctx.parsed.x, this.lang), note: this.mediaSampleNote(rows[ctx.dataIndex]) })}`;
					},
				},
			},
		},
		scales: {
			x: { min: -100, max: 100, grid: { color: (ctx: { tick: { value: number } }) => (ctx.tick.value === 0 ? AXIS.baseline : AXIS.grid), lineWidth: (ctx: { tick: { value: number } }) => (ctx.tick.value === 0 ? 1.5 : 1) }, ticks: { font: { size: 10 }, color: AXIS.text, stepSize: 50, callback: (v: number | string) => formatSignedAuto(Number(v), this.lang) } },
			y: { grid: { display: false }, ticks: { font: { size: 10, weight: 'bold' as const }, color: AXIS.text } },
		},
	};

	// --- Widget 7: Media & topic matrix ---

	get mediaTopicColumns(): string[] {
		return (this.data?.mediaByTopic?.columns ?? []).slice(0, 5).map((c) => translateTopic(c.label || c.key, this.translate));
	}

	get mediaTopicMatrix(): { media: string; values: number[] }[] {
		const cols = (this.data?.mediaByTopic?.columns ?? []).slice(0, 5).map((c) => c.key || '');
		return (this.data?.mediaByTopic?.rows ?? []).slice(0, 5).map((r) => ({ media: r.rowLabel || r.rowKey || '', values: cols.map((key) => r.cellsByColumnKey?.[key]?.mentions ?? 0) }));
	}

	// --- Widget 8: Media sentiment overview ---

	get mediaSentimentData(): MediaSentimentRowView[] {
		return (this.data?.topMedia ?? []).slice(0, 8).map((m) => ({
			outlet: m.name || '',
			mentions: m.mentions ?? 0,
			posPct: Math.round(m.positivePercent ?? 0),
			neuPct: Math.round(m.neutralPercent ?? 0),
			negPct: Math.round(m.negativePercent ?? 0),
			noData: m.positivePercent == null,
			lowData: isLowDataCount(m.mentions),
		}));
	}

	// --- Widget 9: Influential stories ---

	get topStories(): TopStoryView[] {
		return (this.data?.topStories ?? []).map((s) => ({
			title: s.headline || '',
			media: s.outletName || '',
			brand: s.brandTagName || '',
			reach: s.reach != null ? `${formatAnalyticsNumber(s.reach, this.lang, 1)}/100` : '—',
			impact: s.impact != null ? formatSignedAuto(s.impact, this.lang) : '—',
			impactTone: signTone(s.impact ?? 0),
			sentiment: s.sentiment === 'POSITIVE' ? 'pos' : s.sentiment === 'NEGATIVE' ? 'neg' : 'neu',
		}));
	}

	sentimentIcon(sentiment: TopStoryView['sentiment']): string {
		if (sentiment === 'pos') return 'sentiment_satisfied';
		if (sentiment === 'neg') return 'sentiment_dissatisfied';
		return 'sentiment_neutral';
	}

	sentimentIconColorClass(sentiment: TopStoryView['sentiment']): string {
		if (sentiment === 'pos') return 'text-positive';
		if (sentiment === 'neg') return 'text-negative';
		return 'text-ink-subtle';
	}
}
